import { useState, useEffect, useCallback } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Link } from "@chakra-ui/react"
import { GoogleLogo } from "@/components"
import { useNavigate, useSearchParams } from "react-router-dom"
import { signInWithPopup, createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { appFirebaseAuth, googleProvider } from '@/context';
import { useAuth } from '../../context/AuthContext';
import { AppLoader as AppLoaderAnimation } from '@/components/Loaders';
// Analytics temporarily disabled
// import { useAnalytics } from '@travel-dashboard/shared';

interface AuthCardProps {
  title: string;
  description: string;
  buttonText: string;
  isSignup: boolean;
  toggleText: string;
  toggleLink: string;
}

export const AuthCard = ({
  title,
  description,
  buttonText,
  isSignup,
  toggleText,
  toggleLink
}: AuthCardProps): React.ReactNode => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');
  const isDemo = searchParams.get('demo') === 'true';
  const [loaded, setLoaded] = useState(false);
  const { firebaseUser, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  // PostHog analytics - temporarily disabled
  // const { trackLoginFlow, trackClick, trackFormEvent } = useAnalytics();
  const trackLoginFlow = useCallback((..._args: any[]) => {}, []);
  const trackClick = useCallback((..._args: any[]) => {}, []);
  const trackFormEvent = useCallback((..._args: any[]) => {}, []);

  useEffect(() => {
    if (loading) return;

    // Only redirect if user is logged in, not in demo mode, and we're not on the invite page already
    if (firebaseUser && !isDemo && !window.location.pathname.startsWith('/invite/')) {
      // If there's an invite code, redirect to invite page
      if (inviteCode) {
        navigate(`/invite/${inviteCode}`);
      } else {
        navigate('/app');
      }
    }

    setLoaded(true);

    // Track login page load
    trackLoginFlow('started', undefined, undefined);
  }, [navigate, loading, firebaseUser, inviteCode, isDemo, trackLoginFlow]);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Track form submission attempt
    trackFormEvent(isSignup ? 'signup_form' : 'login_form', 'started');

    try {
      let result;
      if (isSignup) {
        result = await createUserWithEmailAndPassword(appFirebaseAuth, email, password);
      } else {
        result = await signInWithEmailAndPassword(appFirebaseAuth, email, password);
      }
      console.log(`User ${isSignup ? 'signed up' : 'logged in'}:`, result);
      // AuthContext automatically handles user sync via onAuthStateChanged

      // Track successful login
      trackLoginFlow('completed', 'email');
      trackFormEvent(isSignup ? 'signup_form' : 'login_form', 'completed');
    } catch (error) {
      console.error('Error during email auth:', error);
      const errorMessage = (error as Error).message;
      setError(`Error: ${errorMessage}`);

      // Track failed login
      trackLoginFlow('failed', 'email', errorMessage);
      trackFormEvent(isSignup ? 'signup_form' : 'login_form', 'error', errorMessage);
    }
  };

  const handleGoogleLogin = async () => {
    // Track Google login button click
    trackClick('google_login_button', 'button', { login_method: 'google' });
    trackLoginFlow('google_clicked', 'google');

    try {
      const result = await signInWithPopup(appFirebaseAuth, googleProvider);
      console.log('User logged in with Google:', result);
      // AuthContext automatically handles user sync via onAuthStateChanged

      // Track successful Google login
      trackLoginFlow('completed', 'google');
    } catch (error) {
      console.error('Error during Google login:', error);
      const errorMessage = (error as Error).message;
      setError(`Error: ${errorMessage}`);

      // Track failed Google login
      trackLoginFlow('failed', 'google', errorMessage);
    }
  };

  if (!loaded) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <AppLoaderAnimation />
      </div>
    )
  }

  return (
    <Card className="w-full max-w-sm">
      <CardHeader>
        <CardTitle className="text-2xl">{title}</CardTitle>
        <CardDescription>
          {inviteCode ? (
            <>You've been invited to join a trip! {description}</>
          ) : (
            description
          )}
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleEmailAuth}>
        <CardContent className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input 
              id="email" 
              type="email" 
              placeholder="m@example.com" 
              required 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="password">Password</Label>
            <Input 
              id="password" 
              type="password" 
              required 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {!isSignup && (
            <Link href="/forgot-password" className="text-sm text-blue-600 hover:underline">
              Forgot password?
            </Link>
          )}
          {error && <p className="text-red-500 text-sm">{error}</p>}
        </CardContent>
        <CardFooter style={{flexDirection: 'column'}} className="gap-4">
          <Button type="submit" className="w-full">{buttonText}</Button>
          <div className="text-sm text-center">
            {toggleText} <Link href={inviteCode ? `${toggleLink}?invite=${inviteCode}` : toggleLink} className="text-blue-600 hover:underline">{isSignup ? 'Sign in' : 'Sign up'}</Link>
          </div>
          <div className="flex items-center gap-2 w-full">
            <hr className="flex-grow border-gray-300" />
            <span className="text-gray-500">or</span>
            <hr className="flex-grow border-gray-300" />
          </div>
          <Button type="button" size="sm" className="w-full gap-1" onClick={handleGoogleLogin}>
            Continue with Google
            <GoogleLogo className="h-4 w-4" />
          </Button>
        </CardFooter>
      </form>
    </Card>
  )
}